import {Component, Input, NgZone, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {IVeranstaltung} from 'src/app/models/IVeranstaltung.interface';
import {VeranstaltungenService} from 'src/app/service/veranstaltungen.service';
import {TeilnehmerService} from 'src/app/service/teilnehmer.service';
import {
  getTeilnehmerRegisterFields,
  getTeilnehmerSearchCategories,
  ITeilnehmer,
  RegisterLine
} from 'src/app/models/ITeilnehmer.interface';
import {Location} from '@angular/common';
import {ToastrService} from 'ngx-toastr';
import {EventQueueService} from 'src/app/service/event-queue-service.service';
import {IStamm} from 'src/app/models/IStamm.interface';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {DetailsTnComponent} from 'src/app/teilnehmer/details-tn/details-tn.component';
import {SessionService} from 'src/app/service/session.service';
import {Rolle} from 'src/app/models/Rolle.enum';
import {FilterEngineService} from 'src/app/service/filter-engine.service';
import {IActionButton} from 'src/app/utils/filter/buttons';
import {Icon} from "../../../utils/icon/icon.component";
import {NachweisvorlageService} from "../../../service/nachweisvorlage.service";
import {INachweisvorlage} from "../../../models/INachweisvorlage.interface";
import {INachweis} from "../../../models/INachweis.interface";
import {addGueltigkeitsdauer} from "../../../models/gueltigkeitsdauerUnit.enum";
import {PdfGeneratorService} from "../../../service/pdf-generator.service";
import {StammNamePipe} from "../../../filters/stamm-name.pipe";
import {TeilnehmerlisteTitelComponent} from "../../../utils/teilnehmerliste-titel/teilnehmerliste-titel.component";

interface IAnmeldung {
  tn: ITeilnehmer;
  selected: boolean;
  missingFields?: string;
}

@Component({
  selector: 'app-event-anmeldung',
  templateUrl: './event-anmeldung.component.html',
  styleUrls: ['./event-anmeldung.component.scss']
})
export class EventAnmeldungComponent implements OnChanges, OnInit {


  @Input()
  public eventId: number;
  public event: IVeranstaltung;
  public groupAnm: Map<IStamm, IAnmeldung[]> = new Map();
  public sortetStaemme: IStamm[] = [];
  public categories: any;
  public query = "";
  public useUrlBarInSearch = false;
  public expandedStaemme: {[stammId: number]: boolean} = {};
  public selectedCountByStamm: {[stammId: number]: number} = {};
  public actionButtons: IActionButton[] = [
    { title: 'Aktualisieren', value: '<i class="fas fa-sync-alt"></i>', callback: () => this.ngOnChanges(), mobile:true, position:"left" },
    { title: 'Nur Angemeldete', value: '<i class="fas fa-square-check"></i>', callback: () => this.query = "angemeldet:ja", mobile:true, position:"right" }
  ];
  public isSL: boolean;

  private fullList: IAnmeldung[] = [];
  private vorlagen: INachweisvorlage[] = [];
  private initialized = false;
  private angemeldeteTeilnehmerIds: Set<number> = new Set();
  private eventStammIds: Set<number> = new Set();
  private nutzerStammIds: Set<number> = new Set();
  private pflichtfeldKeys: Set<string> = new Set();
  private nachweisVorlageIds: number[] = [];
  private vorlagenById: Map<number, INachweisvorlage> = new Map();
  private shouldAutoExpandSingleStamm = false;

  constructor(
    private veranstaltungenService: VeranstaltungenService,
    private location: Location,
    private teilnehmerService: TeilnehmerService,
    private toastr: ToastrService,
    private eventQueue: EventQueueService,
    private ngbModal: NgbModal,
    private filter: FilterEngineService,
    private sessionService: SessionService,
    private zone: NgZone,
    private vorlagenService: NachweisvorlageService,
    private pdfService: PdfGeneratorService
  ) {
    this.categories = getTeilnehmerSearchCategories();
  }

  public ngOnInit() {
    this.initialized = true;
    this.loadEvent();
    if (this.event) {
      this.refreshList();
    }
  }

  private refreshList() {
    this.vorlagen = this.vorlagenService.getNachweisvorlageList();
    this.isSL = this.sessionService.session.currentPermission === Rolle.SL;
    this.updateLookupCaches();
    this.shouldAutoExpandSingleStamm = true;
    this.fullList = this.filterTns().map(tn => {
      let angemeldet = this.angemeldeteTeilnehmerIds.has(tn.id);
      return {
        tn: {
          ...tn,
          angemeldet: angemeldet
        },
        selected: angemeldet,
        missingFields: this.missingFields(tn)
      };
    });
    this.onFilterChange(this.query);
  }

  private loadEvent() {
    this.event = this.veranstaltungenService.getEvent(this.eventId);
  }

  private updateLookupCaches(): void {
    this.angemeldeteTeilnehmerIds = new Set((this.event.teilnehmer || [])
      .filter(tn => !!tn)
      .map(tn => tn.id));
    this.eventStammIds = new Set((this.event.staemme || []).map(stamm => stamm.id));
    this.nutzerStammIds = new Set((this.sessionService.session.nutzer.stamme || []).map(stamm => stamm.id));
    this.pflichtfeldKeys = new Set((this.event.pflichtfelder || "")
      .split(";")
      .filter(key => key.length > 0));
    this.nachweisVorlageIds = (this.event.nachweise || "")
      .split(";")
      .filter(vorlageId => vorlageId.length > 0)
      .map(vorlageId => parseInt(vorlageId, 10))
      .filter(vorlageId => !isNaN(vorlageId));
    this.vorlagenById = new Map(this.vorlagen.map(vorlage => [vorlage.id, vorlage] as [number, INachweisvorlage]));
  }

  public async onFilterChange(newQuery: string) {
    if (typeof newQuery !== "string") {
      return;
    }
    this.query = newQuery;
    try {
      const tnList = this.fullList.map(a => a.tn);
      const anmeldungByTn = new Map<ITeilnehmer, IAnmeldung>();
      this.fullList.forEach(anmeldung => anmeldungByTn.set(anmeldung.tn, anmeldung));
      const filteredList = await this.filter.filter(newQuery, tnList);
      this.zone.run(() => {
        const filteredAnmeldungen = filteredList
          .map(tn => anmeldungByTn.get(tn))
          .filter((anmeldung): anmeldung is IAnmeldung => !!anmeldung);
        this.groupAnm = this.groupAnmeldungen(filteredAnmeldungen);
        this.sortetStaemme = Array.from(this.groupAnm.keys()).sort((s1,s2) => {
         return s1.name.localeCompare(s2.name);
        });
        this.updateStammSelectionCounts();
        this.expandSingleStammOnLoad();
      });
    }
    catch (error) {
      console.log('Failed to filter: ', error);
    }
  }

  private groupAnmeldungen(anmeldungen: IAnmeldung[]): Map<IStamm, IAnmeldung[]> {
    const map = new Map();
    anmeldungen.forEach(a => {
      if(!map.get(a.tn.stamm)){
        map.set(a.tn.stamm, []);
      }
      map.get(a.tn.stamm).push(a);
    });
    return map;
  }

  private filterTns(): ITeilnehmer[] {
    const istVam = this.binIchVam();
    const result = this.teilnehmerService.getTnList().filter(tn => {
      if (this.istTnInMeinemStamm(tn) && this.kannInDasEvent(tn)) {
        return true;
      }
      if (istVam && this.angemeldeteTeilnehmerIds.has(tn.id)) {
        return true;
      }
      return false;
    });

    return result.sort((tn1,tn2) => {
      const d = tn1.nachname.localeCompare(tn2.nachname);
      if(d === 0){
        return tn1.vorname.localeCompare(tn2.vorname);
      }
      return d;
    });
  }

  public setSort(newSortKey: string) {
    const elements = this.query.split(" ");
    const withoutSearch = elements.filter(e => !e.startsWith("$"));
    this.query = withoutSearch.join(" ") + " " + newSortKey;
  }

  public toggleStamm(stamm: IStamm): void {
    this.expandedStaemme[stamm.id] = !this.expandedStaemme[stamm.id];
  }

  public isStammExpanded(stamm: IStamm): boolean {
    return !!this.expandedStaemme[stamm.id];
  }

  public updateStammSelectionCount(stamm: IStamm): void {
    this.selectedCountByStamm[stamm.id] = (this.groupAnm.get(stamm) || []).filter(anmeldung => anmeldung.selected).length;
  }

  private expandSingleStammOnLoad(): void {
    if (!this.shouldAutoExpandSingleStamm) {
      return;
    }
    this.shouldAutoExpandSingleStamm = false;
    if (this.sortetStaemme.length === 1) {
      this.expandedStaemme[this.sortetStaemme[0].id] = true;
    }
  }

  private updateStammSelectionCounts(): void {
    this.selectedCountByStamm = {};
    this.sortetStaemme.forEach(stamm => this.updateStammSelectionCount(stamm));
  }

  public trackByStammId(index: number, stamm: IStamm): number {
    return stamm.id;
  }

  public trackByAnmeldungId(index: number, anmeldung: IAnmeldung): number {
    return anmeldung.tn.id;
  }

  ngOnChanges(changes?: SimpleChanges) {
    this.loadEvent();
    if (!this.initialized) {
      return;
    }
    if (!changes || changes.eventId) {
      if (this.event) {
        this.refreshList();
      }
    }
  }

  async save() {
    try {
      console.log("Speichern");
      const result = await this.veranstaltungenService.updateTnOnEvent(
        this.fullList.filter(an => !an.selected).map(an => an.tn),
        this.fullList.filter(an => an.selected && this.istTnInMeinemStamm(an.tn)).map(an => an.tn),
        this.event);
      const removeResult = result[0];
      const addResult = result[1];
      this.toastr.success(removeResult + "<br/><br/>" + addResult, "Gespeichert");
    } catch (e) {
      this.toastr.error(e[0] + "<br/><br/>" + e[1], "Fehler beim Speichern");
    } finally {
      this.eventQueue.fireEvent("relaod");
    }
  }

  binIchVam() {
    return this.event.veranstaltungsmanager.some(vamId => this.sessionService.session.nutzer.id === vamId.id)
  }

  istTnInMeinemStamm(tn: ITeilnehmer) {
    const currentPermission = this.sessionService.session.currentPermission;
    if (currentPermission === Rolle.ADM || currentPermission === Rolle.SU) {
      return true;
    }
    return this.nutzerStammIds.has(tn.stamm.id);
  }

  kannInDasEvent(tn: ITeilnehmer): boolean {
    if(tn.postleitzahl === -1){
      return false;
    }
    if (this.event.veranstalter.id === tn.stamm.id) {
      return true;
    }
    if (this.eventStammIds.has(tn.stamm.id)) {
      return true;
    }
    if (this.event.veranstaltungstyp === "2") {//REGP Event
      return true;
    }

    return false;
  }

  public printTeilnehmerlisteStamm(stamm: IStamm) {
    this.askTitel().then(async titel => {
      if (titel === null) {
        return;
      }
      try {
        const sichtbareTnIds = new Set((this.groupAnm.get(stamm) || []).map(an => an.tn.id));
        const subList = (await this.veranstaltungenService.getPricesToEvent(this.event))
          .filter(a => !!a.teilnehmer && sichtbareTnIds.has(a.teilnehmer.id))
          .sort((a1, a2) => {
            const d = a1.teilnehmer.nachname.localeCompare(a2.teilnehmer.nachname);
            if (d === 0) {
              return a1.teilnehmer.vorname.localeCompare(a2.teilnehmer.vorname);
            }
            return d;
          });
        const stammName = new StammNamePipe().transform(stamm);
        const pdf = this.pdfService.generateTeilnehmerliste(this.event, subList, titel, stammName);
        pdf.download(`Teilnehmerliste_${this.event.id}_${this.cleanFilename(stammName)}.pdf`);
      } catch (e) {
        this.toastr.error("Teilnehmerliste konnte nicht erstellt werden.", "Fehler");
      }
    });
  }

  private askTitel(): Promise<string | null> {
    const modal = this.ngbModal.open(TeilnehmerlisteTitelComponent, { size: 'lg' });
    return modal.result.then(
      (titel: string) => titel,
      () => null
    );
  }

  private cleanFilename(raw: string): string {
    return raw.replace(/[^-a-zA-Z0-9_.()äöüß ]/g, '_');
  }

  cancel() {
    console.log("Abbrechen");
    this.location.back();
  }

  public open(tnId: number) {
    const activeModal = this.ngbModal.open(DetailsTnComponent, {
      size: 'lg'
    });
    activeModal.componentInstance.tnId = tnId;
  }

  private missingFields(tn: ITeilnehmer): string | undefined {
    const allFields = getTeilnehmerRegisterFields();
    const ret: string[] = [];
    for(const rf of allFields) {
      if(RegisterLine.isRegisterLine(rf)){
        //Mir egal
      } else {
        rf.pflichtfeld = this.pflichtfeldKeys.has(rf.key);
        if(rf.pflichtfeld) {
          if(rf.type === "boolean") {
            if(!tn[rf.key]) {
              ret.push("Voraussetzung nicht erfüllt: " + rf.title)
            }
          } else {
            if(!tn[rf.key]) {
              ret.push(rf.title + " muss angegeben werden.")
            }
          }
        }
      }
    }
    for(const vorlageId of this.nachweisVorlageIds) {
      const matchingNachweise = tn.nachweise.filter(n => n.vorlage.id === vorlageId);

      const validAtStart = matchingNachweise.some(n => this.endDateRaw(n) > this.event.anfangsdatum);
      const validNow = matchingNachweise.some(n => this.endDateRaw(n) > new Date());

      const vorlage = this.vorlagenById.get(vorlageId);
      const begriff = vorlage && vorlage.typ === 'SCHULUNG' ? 'Schulung' : 'Nachweis';
      const name = vorlage ? vorlage.name : vorlageId;
      if(!validNow) {
        ret.push(begriff + " " + name + " fehlt.");
      } else if(!validAtStart) {
        ret.push(begriff + " " + name + " wird vorher ablaufen.");
      }

    }
    if(ret.length == 0) {
      return undefined;
    }
    return ret.join("<br>");
  }

  endDateRaw(nachweis: INachweis): Date {
    if(nachweis.vorlage.gueltigkeitsdauerVal <= 0) {
      return new Date(9999, 12, 31, 23, 59, 59);
    }
    return addGueltigkeitsdauer(nachweis.ausstellungsdatum, nachweis.vorlage.gueltigkeitsdauerUnit as any, nachweis.vorlage.gueltigkeitsdauerVal)
  }

  public readonly Icon = Icon;
}
